import {testIdProps} from '@github-ui/test-id-props'
import {clsx} from 'clsx'
import {useCallback, useMemo, useState} from 'react'

import type {Iteration} from '../../../api/columns/contracts/iteration'
import {MemexColumnDataType} from '../../../api/columns/contracts/memex-column'
import type {PersistedOption} from '../../../api/columns/contracts/single-select'
import {
  AutoAssignWorkflowToggleName,
  AutoAssignWorkflowToggleUI,
  GroupByCollapseToggleStatKey,
  GroupHide,
} from '../../../api/stats/contracts'
import type {ClientMemexWorkflow} from '../../../api/workflows/contracts'
import {useAutoAssignAgentEnabled} from '../../../features/auto-assign-agent/use-auto-assign-agent-enabled'
import {useHorizontalGroupedBy} from '../../../features/grouping/hooks/use-horizontal-grouped-by'
import type {GroupingMetadataWithSource} from '../../../features/grouping/types'
import {getGroupTestId, resolveRawTitleForGroupingContext} from '../../../helpers/table-group-utilities'
import {emojiImgToText} from '../../../helpers/util'
import {ViewType} from '../../../helpers/view-type'
import {
  AutoAssignAgentColumnWorkflowDefinition,
  isAutoAssignAgentWorkflow,
  isWorkflowPersisted,
  workflowWithColumnFieldOption,
} from '../../../helpers/workflow-utilities'
import {usePostStats} from '../../../hooks/common/use-post-stats'
import {useColumnWorkflowStatesByFieldOptionId} from '../../../hooks/use-column-workflow-states-by-field-option-id'
import {useDeleteGroup} from '../../../hooks/use-delete-group'
import {useMoveItemsToIteration} from '../../../hooks/use-move-items-to-iteration'
import {useViewType} from '../../../hooks/use-view-type'
import {isIterationColumnModel} from '../../../models/column-model/guards'
import type {MemexItemModel} from '../../../models/memex-item-model'
import {useAllColumns} from '../../../state-providers/columns/use-all-columns'
import {useUpdateOptions} from '../../../state-providers/columns/use-update-options'
import {useWorkflows} from '../../../state-providers/workflows/use-workflows'
import {SingleSelectOptionModal} from '../../fields/single-select/single-select-option-modal'
import {normalizeToFilterName} from '../../filter-bar/helpers/search-filter'
import {useSearch} from '../../filter-bar/search-context'
import {ColumnWorkflowIndicator} from '../column-workflow-indicator'
import {GroupMenu} from '../group-menu'
import {MoveItemsToIterationDialog} from '../move-items-to-iteration-menu'
import styles from './group-header.module.css'
import {GroupHeaderExpansionButton} from './group-header-expansion-button'
import {GROUP_HEADER_HEIGHT, StyledGroupHeader} from './styled-group-header'

type Props = {
  /**
   * Indicate whether the group is marked as "expanded" (showing items) or
   * "collapsed" (items hidden).
   *
   * See `TableGroupContents` for rendering the rest of a group.
   */
  isCollapsed: boolean
  /**
   * Indicate whether the group should be highlighted or not.
   */
  isHighlighted?: boolean
  /**
   * Metadata related to the provided group.
   */
  metadata: GroupingMetadataWithSource
  /**
   * The items in this group.
   */
  itemsInGroup: ReadonlyArray<MemexItemModel>
  /**
   * Whether the group header should be sticky.
   */
  sticky?: boolean
  /**
   * Ref to the header element of the view (i.e. swimlane header for board, table header with column names for table).
   */
  viewHeaderRef?: React.RefObject<HTMLElement | null> | null
  ref?: React.Ref<HTMLDivElement>
} & React.HTMLAttributes<HTMLDivElement>

/**
 * Header component for a group when viewing the project in table mode
 */
export const GroupHeader = ({
  ref,
  isCollapsed,
  isHighlighted,
  metadata,
  itemsInGroup,
  children,
  sticky,
  viewHeaderRef,
  className,
  ...props
}: Props) => {
  const {sourceObject} = metadata
  const {groupedByColumnId, groupedByColumn, toggleGroupCollapsed} = useHorizontalGroupedBy()
  const {postStats} = usePostStats()
  const {viewType} = useViewType()
  const title = resolveRawTitleForGroupingContext(sourceObject)
  const testId = getGroupTestId(sourceObject)

  const [isEditingDetails, setIsEditingDetails] = useState(false)
  const isSingleSelectGroup =
    groupedByColumn?.dataType === MemexColumnDataType.SingleSelect &&
    sourceObject.dataType === MemexColumnDataType.SingleSelect &&
    sourceObject.kind !== 'empty'
  const singleSelectOption = isSingleSelectGroup ? sourceObject.value.option : null

  const isIterationGroup =
    groupedByColumn?.dataType === MemexColumnDataType.Iteration &&
    sourceObject.dataType === MemexColumnDataType.Iteration &&
    sourceObject.kind !== 'empty'
  const isEmptyGroup = sourceObject.kind === 'empty'
  const isGroupedByIssueField = groupedByColumn?.isIssueField
  const isMilestoneGroup =
    groupedByColumn?.dataType === MemexColumnDataType.Milestone &&
    sourceObject.dataType === MemexColumnDataType.Milestone &&
    sourceObject.kind !== 'empty'

  const milestoneURL = isMilestoneGroup ? sourceObject.value?.url : null

  const onCollapseToggle = useCallback(() => {
    const newState = !isCollapsed

    if (groupedByColumnId) {
      postStats({
        key: GroupByCollapseToggleStatKey,
        name: GroupByCollapseToggleStatKey,
        groupTitle: emojiImgToText(title),
        memexProjectColumnId: groupedByColumnId,
        collapsed: newState,
        numberOfRows: itemsInGroup.length,
      })
    }
    toggleGroupCollapsed(metadata.value)
  }, [isCollapsed, groupedByColumnId, toggleGroupCollapsed, metadata, postStats, title, itemsInGroup])

  const {updateColumnOption} = useUpdateOptions()
  const submitEditColumnDetails = (updated: PersistedOption) => {
    if (!isSingleSelectGroup) return
    updateColumnOption(groupedByColumn, updated)
    setIsEditingDetails(false)
  }

  const isHideable = groupedByColumn !== undefined && metadata.sourceObject.kind !== 'empty'
  const {insertFilter} = useSearch()
  const hideGroup = useCallback(() => {
    if (!isHideable) return

    insertFilter(`-${normalizeToFilterName(groupedByColumn.name)}`, title)

    postStats({
      name: GroupHide,
      context: JSON.stringify(metadata.sourceObject),
    })
  }, [isHideable, insertFilter, groupedByColumn, title, postStats, metadata.sourceObject])

  const {deleteGroup} = useDeleteGroup(groupedByColumn)
  const onDelete = useMemo(
    () =>
      deleteGroup &&
      (() => {
        if (isSingleSelectGroup) deleteGroup(sourceObject.value.option.id)
        else if (isIterationGroup) deleteGroup(sourceObject.value.iteration.id)
      }),
    [deleteGroup, isIterationGroup, isSingleSelectGroup, sourceObject],
  )

  const [groupMenuOpen, setGroupMenuOpen] = useState(false)
  const [iterationPickerOpen, setIterationPickerOpen] = useState(false)
  const {moveItemsToIteration} = useMoveItemsToIteration()
  const {allColumns} = useAllColumns()

  const currentIterationId = isIterationGroup ? sourceObject.value.iteration.id : undefined

  const onMoveItems = useMemo(() => {
    if (!isIterationGroup || !groupedByColumnId) return undefined

    // Look up the fresh column from allColumns (reactive state) rather than
    // groupedByColumn (stale snapshot) so newly added iterations are reflected
    // without a page refresh.
    const freshColumn = allColumns.find(c => c.id === groupedByColumnId)
    if (!freshColumn || !isIterationColumnModel(freshColumn)) return undefined

    const {iterations = []} = freshColumn.settings.configuration
    const hasOtherIterations = iterations.some(iter => iter.id !== currentIterationId)
    if (!hasOtherIterations) return undefined

    return () => {
      setIterationPickerOpen(true)
    }
  }, [isIterationGroup, groupedByColumnId, allColumns, currentIterationId])

  const handleMoveToIteration = useCallback(
    async (iteration: Iteration) => {
      if (!groupedByColumnId) return
      // Use fresh column data so newly added iterations are reflected without refresh
      const freshCol = allColumns.find(c => c.id === groupedByColumnId)
      if (!freshCol || !isIterationColumnModel(freshCol)) return
      await moveItemsToIteration(itemsInGroup, iteration, freshCol.id, !!freshCol.isIssueField)
    },
    [moveItemsToIteration, itemsInGroup, allColumns, groupedByColumnId],
  )

  const onCancelSingleSelectModal = useCallback(() => {
    setIsEditingDetails(false)
    setGroupMenuOpen(true)
  }, [setIsEditingDetails, setGroupMenuOpen])

  const expandCollapseLabel = isCollapsed ? `Expand group ${title}` : `Collapse group ${title}`

  const ariaRole = viewType === ViewType.Board ? undefined : 'cell'
  const {handleEnabledChangeRequest, navigateToWorkflow, replaceWorkflowInLocalState, workflows} = useWorkflows()
  const autoAssignAgentEnabled = useAutoAssignAgentEnabled()
  const autoAssignWorkflow = useMemo(() => {
    if (!autoAssignAgentEnabled) return undefined
    return workflows.find(workflow => isAutoAssignAgentWorkflow(workflow))
  }, [autoAssignAgentEnabled, workflows])
  const hasConfiguredAutoAssignWorkflow = useMemo(() => {
    if (!autoAssignAgentEnabled) return false
    return workflows.some(workflow => isAutoAssignAgentWorkflow(workflow) && isWorkflowPersisted(workflow))
  }, [autoAssignAgentEnabled, workflows])
  const columnWorkflowStatesByFieldOptionId = useColumnWorkflowStatesByFieldOptionId(groupedByColumn?.databaseId)
  const columnWorkflowStates = useMemo(() => {
    if (
      viewType !== ViewType.Table ||
      !isSingleSelectGroup ||
      groupedByColumn?.databaseId == null ||
      !singleSelectOption
    ) {
      return []
    }

    return columnWorkflowStatesByFieldOptionId.get(singleSelectOption.id) ?? []
  }, [
    columnWorkflowStatesByFieldOptionId,
    groupedByColumn?.databaseId,
    isSingleSelectGroup,
    singleSelectOption,
    viewType,
  ])
  const openColumnWorkflow = useCallback(
    (workflow: ClientMemexWorkflow) => {
      navigateToWorkflow(workflow.clientId)
    },
    [navigateToWorkflow],
  )
  const configureWorkflows = useCallback(() => {
    navigateToWorkflow()
  }, [navigateToWorkflow])
  const toggleColumnWorkflowEnabled = useCallback(
    async (workflow: ClientMemexWorkflow, enabled: boolean) => {
      if (isAutoAssignAgentWorkflow(workflow)) {
        postStats({
          name: AutoAssignWorkflowToggleName,
          ui: AutoAssignWorkflowToggleUI.GroupHeaderMenu,
          context: enabled ? 'enable' : 'disable',
        })
      }
      await handleEnabledChangeRequest.perform({workflow, enable: enabled})
    },
    [handleEnabledChangeRequest, postStats],
  )
  const addAutoAssignWorkflow = useCallback(() => {
    if (!autoAssignWorkflow || groupedByColumn?.databaseId == null || !singleSelectOption) return

    setGroupMenuOpen(false)
    const workflowForColumn = workflowWithColumnFieldOption(
      autoAssignWorkflow,
      AutoAssignAgentColumnWorkflowDefinition,
      groupedByColumn.databaseId,
      singleSelectOption.id,
    )
    replaceWorkflowInLocalState(workflowForColumn)
    navigateToWorkflow(workflowForColumn.clientId)
  }, [autoAssignWorkflow, groupedByColumn, navigateToWorkflow, replaceWorkflowInLocalState, singleSelectOption])
  const canAddAutoAssignWorkflow =
    viewType === ViewType.Table &&
    isSingleSelectGroup &&
    groupedByColumn?.databaseId != null &&
    singleSelectOption &&
    autoAssignWorkflow &&
    !hasConfiguredAutoAssignWorkflow

  return (
    <>
      <StyledGroupHeader
        className={clsx(
          {
            collapsed: isCollapsed,
            board: viewType === ViewType.Board,
            sticky,
            highlighted: isHighlighted,
          },
          className,
        )}
        // eslint-disable-next-line react-hooks/refs
        topOffset={viewHeaderRef?.current?.getBoundingClientRect().height ?? 0}
        {...testIdProps(`group-header-${testId}`)}
        ref={ref}
        {...props}
      >
        <div
          role={ariaRole}
          className={clsx(
            {
              board: viewType === ViewType.Board,
              roadmap: viewType === ViewType.Roadmap,
            },
            styles.groupHeaderContainer,
          )}
          style={{
            height: `${GROUP_HEADER_HEIGHT}px`,
          }}
        >
          <GroupHeaderExpansionButton
            onCollapseToggle={onCollapseToggle}
            isCollapsed={isCollapsed}
            expandCollapseLabel={expandCollapseLabel}
            {...testIdProps(`group-by-toggle-collapsed-${testId}`)}
          />
          <div className={styles.groupHeaderContent}>{children}</div>
          {columnWorkflowStates.length > 0 && (
            <ColumnWorkflowIndicator
              columnName={title}
              workflowStates={columnWorkflowStates}
              onConfigureWorkflows={configureWorkflows}
              onOpenWorkflow={openColumnWorkflow}
              onToggleWorkflowEnabled={toggleColumnWorkflowEnabled}
            />
          )}
          <GroupMenu
            name={title}
            items={itemsInGroup}
            open={groupMenuOpen}
            setOpen={setGroupMenuOpen}
            fieldId={groupedByColumnId}
            onEditDetails={isSingleSelectGroup && !isGroupedByIssueField ? () => setIsEditingDetails(true) : undefined}
            showEditField={isIterationGroup}
            showEditMilestones={isMilestoneGroup}
            milestoneURL={milestoneURL}
            onAddAutoAssignWorkflow={canAddAutoAssignWorkflow ? addAutoAssignWorkflow : undefined}
            onHide={isHideable ? hideGroup : undefined}
            onDelete={!isEmptyGroup ? onDelete : undefined}
            onMoveItems={onMoveItems}
          />
        </div>
      </StyledGroupHeader>
      {isEditingDetails && singleSelectOption && (
        <SingleSelectOptionModal
          initialOption={singleSelectOption}
          onCancel={onCancelSingleSelectModal}
          onSave={updatedOption => submitEditColumnDetails({...singleSelectOption, ...updatedOption})}
          noDuplicatedOptions={false}
          enableNameLengthValidation={false}
        />
      )}
      {iterationPickerOpen && groupedByColumnId != null && (
        <MoveItemsToIterationDialog
          iterationColumnId={groupedByColumnId}
          currentIterationId={currentIterationId}
          onSelect={handleMoveToIteration}
          onClose={() => setIterationPickerOpen(false)}
        />
      )}
    </>
  )
}

GroupHeader.displayName = 'GroupHeader'
